import React, { useEffect } from "react";
import { useNavigate } from "../i18n/router";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft, CreditCard, Loader2, Banknote, Wallet, Lock, History,
  TrendingUp, Users, CheckCircle2
} from "lucide-react";

const Bullet = ({ icon: Icon, children }) => (
  <li className="flex items-start gap-3 text-sm" style={{ color: "var(--eunoia-deep)" }}>
    <Icon className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: "var(--eunoia-accent)" }} />
    <span>{children}</span>
  </li>
);

const RoadmapCard = ({ status, title, description, dataTestid }) => {
  const styles = status === "available"
    ? {
        bg: "var(--eunoia-surface-soft)",
        border: "1px solid var(--eunoia-whisper)",
        iconBg: "var(--eunoia-accent-whisper)",
        iconColor: "var(--eunoia-accent)",
        Icon: CheckCircle2,
        pillText: "Доступно",
        pillBg: "var(--eunoia-accent-whisper)",
        pillColor: "var(--eunoia-accent)",
      }
    : {
        bg: "var(--eunoia-surface-soft)",
        border: "1px dashed var(--eunoia-whisper)",
        iconBg: "var(--eunoia-whisper)",
        iconColor: "var(--eunoia-soft)",
        Icon: Lock,
        pillText: "Скоро",
        pillBg: "var(--eunoia-whisper)",
        pillColor: "var(--eunoia-soft)",
      };

  return (
    <div
      data-testid={dataTestid}
      className="rounded-2xl p-5 flex items-start gap-4"
      style={{ backgroundColor: styles.bg, border: styles.border }}
    >
      <div
        className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center"
        style={{ backgroundColor: styles.iconBg }}
      >
        <styles.Icon className="h-5 w-5" style={{ color: styles.iconColor }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <h4 className="text-sm font-semibold" style={{ color: "var(--eunoia-deep)" }}>{title}</h4>
          <span
            className="text-[10px] font-medium px-2 py-0.5 rounded-full"
            style={{ backgroundColor: styles.pillBg, color: styles.pillColor }}
          >
            {styles.pillText}
          </span>
        </div>
        {description && (
          <p className="text-xs leading-relaxed" style={{ color: "var(--eunoia-soft)" }}>{description}</p>
        )}
      </div>
    </div>
  );
};

export default function OrganizerPaymentsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login?redirect=/organizer/payments");
    }
  }, [user, authLoading, navigate]);

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: "var(--eunoia-accent)" }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen eunoia-space-lg px-4 pb-16" data-testid="organizer-payments-page">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 eunoia-caption flex items-center gap-2 hover:opacity-80"
          style={{ color: "var(--eunoia-soft)" }}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Назад
        </button>

        <div className="flex items-center gap-3 mb-8">
          <CreditCard className="h-7 w-7" style={{ color: "var(--eunoia-accent)" }} />
          <h1 className="eunoia-title-large">Платежи</h1>
        </div>

        {/* Coming soon hero */}
        <div className="eunoia-surface p-8 sm:p-12 text-center mb-8">
          <div
            className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
            style={{ backgroundColor: "var(--eunoia-accent-whisper)" }}
          >
            <Wallet className="h-7 w-7" style={{ color: "var(--eunoia-accent)" }} />
          </div>
          <h2 className="eunoia-title-medium mb-4">Управление оплатами скоро появится</h2>
          <p className="eunoia-body max-w-xl mx-auto" style={{ color: "var(--eunoia-soft)" }}>
            В будущем здесь будут доступны:
          </p>

          <ul className="mt-6 max-w-md mx-auto space-y-3 text-left">
            <Bullet icon={CreditCard}>Способы оплаты организатора</Bullet>
            <Bullet icon={Lock}>Подключение Stripe</Bullet>
            <Bullet icon={History}>История платежей</Bullet>
            <Bullet icon={Banknote}>Выплаты организаторам</Bullet>
            <Bullet icon={Users}>Статусы оплат участников</Bullet>
            <Bullet icon={TrendingUp}>Финансовая аналитика мероприятий</Bullet>
          </ul>
        </div>

        {/* Roadmap */}
        <section>
          <h3 className="eunoia-title-small mb-4">Планируемые типы оплаты</h3>
          <div className="space-y-3">
            <RoadmapCard
              status="available"
              title="Бесплатное мероприятие"
              description="Свободный вход — без оплаты"
              dataTestid="roadmap-free"
            />
            <RoadmapCard
              status="available"
              title="Оплата организатору напрямую"
              description="Revolut, банковский перевод, наличные и другие способы — настраивается организатором"
              dataTestid="roadmap-direct"
            />
            <RoadmapCard
              status="soon"
              title="Онлайн-оплата через EUNOIA"
              description="Приём платежей и автоматические переводы организатору — появится позже"
              dataTestid="roadmap-online"
            />
          </div>
        </section>

        <p
          className="text-center mt-10 eunoia-caption"
          style={{ color: "var(--eunoia-soft)" }}
        >
          Мы напишем вам, как только функция станет доступна.
        </p>
      </div>
    </div>
  );
}
